import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { TransitionProps } from '@mui/material/transitions';
import throttle from 'lodash-es/throttle';
import { useModal } from 'react-modal-hook';

import { StatusPageInfo } from 'app/integrations/StatusPageInfo';
import { VOAHeader } from 'components/Promo/VOAHeader';
import { VOAModal } from 'components/Promo/VOAModal';
import { useGetBillingProfile } from 'entities/billing/api/useGetBillingProfile';
import { SubscriptionHeader } from 'features/billing/ui/promo/PromoSubscriptionHeader';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useFeatures } from 'shared/lib/features/useFeatures';
import { useStatusPageIncident } from 'shared/lib/statusPage/useStatusPageIncident';
import { localStorageService, StorageKeys } from 'shared/lib/storage/storageService';
import { urls } from 'shared/urls';

import { ModalType } from '../analytics/modalTypes';
import { useTrackedShowModal } from '../analytics/useTrackedShowModal';
import { type PromoConfig, PromoKeys } from './common';

// Convert Promo to StorageKeys for type safety
const promoToStorageKey = (promo: PromoKeys): StorageKeys => {
  switch (promo) {
    case PromoKeys.VOA_FANNIE_FREDDIE_POPUP:
      return StorageKeys.VOA_FANNIE_FREDDIE_POPUP;
    case PromoKeys.VOA_FANNIE_FREDDIE_HEADER:
      return StorageKeys.VOA_FANNIE_FREDDIE_HEADER;
  }
};

const isPromoActive = (promo: PromoConfig) => {
  if (promo.enabled === false) {
    return false;
  }

  let localPromo = null;

  localPromo = localStorageService.get(promoToStorageKey(promo.id));

  if (localPromo === false) {
    return false;
  }

  const snoozeTime = (promo.interval ?? 0) * 1000;

  if (snoozeTime && localPromo && Date.now() < Number(localPromo) + snoozeTime) {
    return false;
  }

  return true;
};

const setLocalPromo = (id: PromoKeys, value: boolean | string) => {
  localStorageService.set(promoToStorageKey(id), value);
};

const setPromoShown = (id: PromoKeys) => {
  setLocalPromo(id, Date.now().toString());
};

const setPromoClosed = (id: PromoKeys) => {
  setLocalPromo(id, false);
};

const VOA_LINK =
  'https://truv.com/blog/truv-now-offers-automated-asset-verification-through-freddie-mac-loan-product-advisor-as-an-aim-service-provider';

export const usePromo = () => {
  const { data: billingProfile } = useGetBillingProfile();
  const { data: statusPageIncident } = useStatusPageIncident();
  const { permissions } = useAuth();

  const isSubscriptionBannerVisible = useMemo(() => {
    if (!billingProfile) return false;

    if (permissions.has('can_edit_billing') && billingProfile.can_subscribe !== false) {
      return true;
    }

    return false;
  }, [billingProfile, permissions]);

  const { data: features } = useFeatures();
  const promoHeaderRef = useRef<HTMLDivElement>(null);
  const [promoHeaderHeight, setPromoHeaderHeight] = useState<number>(0);

  const allPromos = useMemo(() => features?.promo || [], [features?.promo]);
  const activePromo = useMemo(() => allPromos.find((p) => isPromoActive(p)), [allPromos]);

  const promoHeader = useMemo(() => {
    if (statusPageIncident) {
      return <StatusPageInfo ref={promoHeaderRef} incident={statusPageIncident} />;
    }

    if (isSubscriptionBannerVisible) {
      return <SubscriptionHeader ref={promoHeaderRef} href={urls.subscriptionFlow.url()} />;
    }

    if (activePromo?.id === PromoKeys.VOA_FANNIE_FREDDIE_HEADER) {
      return (
        <VOAHeader
          ref={promoHeaderRef}
          href={activePromo.href ?? VOA_LINK}
          onClick={() => {
            setPromoClosed(PromoKeys.VOA_FANNIE_FREDDIE_HEADER);
            setPromoClosed(PromoKeys.VOA_FANNIE_FREDDIE_POPUP);
          }}
        />
      );
    }

    return null;
  }, [activePromo, statusPageIncident, isSubscriptionBannerVisible]);

  const [showPromoModal, hidePromoModal] = useModal(
    ({ in: open = false }: TransitionProps) => {
      const isActivePromo = activePromo?.id === PromoKeys.VOA_FANNIE_FREDDIE_POPUP;

      if (isActivePromo) {
        return (
          <VOAModal
            href={activePromo.href ?? VOA_LINK}
            open={open}
            onClick={() => {
              hidePromoModal();

              setPromoClosed(PromoKeys.VOA_FANNIE_FREDDIE_POPUP);
              setPromoClosed(PromoKeys.VOA_FANNIE_FREDDIE_HEADER);
            }}
            onClose={hidePromoModal}
          />
        );
      }
    },
    [activePromo],
  );

  const showPromoModalWithAnalytics = useTrackedShowModal(showPromoModal, ModalType.VOA_FANNIE_FREDDIE_POPUP);

  useEffect(() => {
    if (!activePromo?.id) {
      return;
    }

    showPromoModalWithAnalytics();

    setPromoShown(activePromo.id);
  }, [showPromoModalWithAnalytics, activePromo]);

  // Measure header height with resize handling
  useLayoutEffect(() => {
    const measureHeight = () => {
      if (promoHeaderRef.current) {
        const height = promoHeaderRef.current.getBoundingClientRect().height;
        setPromoHeaderHeight(height);
      } else if (promoHeaderHeight > 0) {
        setPromoHeaderHeight(0);
      }
    };

    // Initial measurement
    measureHeight();

    // Create throttled resize handler (300ms throttle)
    const throttledResize = throttle(measureHeight, 300, { trailing: true });

    // Add resize listener
    window.addEventListener('resize', throttledResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', throttledResize);
      throttledResize.cancel();
    };
  }, [promoHeaderRef, isSubscriptionBannerVisible, statusPageIncident]);

  return {
    activePromo,
    promoHeader,
    promoHeaderHeight,
  };
};
